import React, { useContext, useState } from 'react'

import { Button } from '@acorns/grove'
import { useAnalyticsClient, useEnv } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  AiChatWidgetContainerViewedWidget_type,
  AiChatWidgetSupportButtonTappedType,
  trackAiChatWidgetContainerViewedPayload,
  trackAiChatWidgetSupportButtonTappedPayload,
} from 'generated/trackingEvents'
import {
  ChatWidget,
  CustomerSupportChatWidget,
  useUserQueryQuery,
} from 'generated/types'
import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'
import ErrorModal from 'pages/settings/personal/ErrorModal'
import { CHAT_WIDGET_CTA_CONTAINER } from 'utils/testing/testIDs'

import { EmailDrawer } from './EmailDrawer'
import { AIAssistantContext } from './providers/AIAssistantProvider'
import { initializeSalesforceWidget } from './utils/salesforce'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  left: 50px;
  margin-bottom: 20px;
  margin-top: 10px;
  position: relative;
  width: fit-content;
`

const isCustomerSupportChatWidget = (
  widget: ChatWidget,
): widget is CustomerSupportChatWidget => {
  return widget.__typename === 'CustomerSupportChatWidget'
}

export const ChatWidgetContainer = ({
  chatWidgets,
  onCloseAssistant,
  sessionId,
}: {
  chatWidgets: ChatWidget[]
  onCloseAssistant: () => void
  sessionId: string
}) => {
  const { env } = useEnv()
  const salesforceOrgId = env.get('salesforceOrgId')
  const salesforceBaseLiveAgentUrl = env.get('salesforceBaseLiveAgentUrl')
  const salesforceScrt2Url = env.get('salesforceScrt2Url')
  const { data: userData, loading: userLoading } = useUserQueryQuery()
  const [showFallbackModal, setShowFallbackModal] = useState(false)
  const analyticsClient = useAnalyticsClient()
  const { setIsSalesforceWidgetInitialized } = useContext(AIAssistantContext)

  const { track } = useAnalyticsClient()
  const [isEmailDrawerOpen, setIsEmailDrawerOpen] = useState(false)

  const chatWidgetRef = useAnalyticsComponentViewed({
    generatedPayload: trackAiChatWidgetContainerViewedPayload({
      widgetType: AiChatWidgetContainerViewedWidget_type.Support,
    }),
  })

  const handleSalesforceClick = () => {
    if (customerSupportChatWidget?.__typename !== 'CustomerSupportChatWidget') {
      return
    }
    const redirect = customerSupportChatWidget.chatRedirect
    const conversationCategory = redirect.conversationCategory
    const conversationContext = redirect.conversationContext

    const hiddenPrechatFields = {
      firstName: userData?.user?.firstName ?? '',
      lastName: userData?.user?.lastName ?? '',
      email: userData?.user?.email ?? '',
      deviceId: analyticsClient.getAnonymousId(),
      context: conversationContext,
      supportCategory: conversationCategory,
      chatSessionId: sessionId,
    }

    initializeSalesforceWidget({
      hiddenPrechatFields,
      salesforceBaseLiveAgentUrl,
      salesforceOrgId,
      salesforceScrt2Url,
      setIsSalesforceWidgetInitialized,
      setShowFallbackModal,
    })
    onCloseAssistant()
    track(
      ...trackAiChatWidgetSupportButtonTappedPayload({
        type: AiChatWidgetSupportButtonTappedType.Chat,
        ctaTitle:
          (customerSupportChatWidget &&
            isCustomerSupportChatWidget(customerSupportChatWidget) &&
            customerSupportChatWidget.chatRedirect?.title) ||
          '',
      }),
    )
  }

  const customerSupportChatWidget = chatWidgets.find(
    (widget) =>
      isCustomerSupportChatWidget(widget) &&
      (widget.chatRedirect || widget.emailRedirect),
  )

  if (!customerSupportChatWidget || userLoading) {
    return null
  }

  const handleEmailClick = () => {
    setIsEmailDrawerOpen(true)
    track(
      ...trackAiChatWidgetSupportButtonTappedPayload({
        type: AiChatWidgetSupportButtonTappedType.Chat,
        ctaTitle:
          (customerSupportChatWidget &&
            isCustomerSupportChatWidget(customerSupportChatWidget) &&
            customerSupportChatWidget.emailRedirect?.title) ||
          '',
      }),
    )
  }

  return (
    <>
      {showFallbackModal && (
        <ErrorModal
          isOpen={showFallbackModal}
          onRequestClose={() => setShowFallbackModal(false)}
        />
      )}
      <Container data-testid={CHAT_WIDGET_CTA_CONTAINER} ref={chatWidgetRef}>
        {'chatRedirect' in customerSupportChatWidget &&
        customerSupportChatWidget.chatRedirect ? (
          <Button key="chatRedirect" onClick={handleSalesforceClick}>
            {customerSupportChatWidget.chatRedirect.title}
          </Button>
        ) : null}
        {'emailRedirect' in customerSupportChatWidget &&
        customerSupportChatWidget.emailRedirect ? (
          <Button key="emailRedirect" onClick={handleEmailClick}>
            {customerSupportChatWidget.emailRedirect.title}
          </Button>
        ) : null}
        <EmailDrawer
          isEmailDrawerOpen={isEmailDrawerOpen}
          setIsEmailDrawerOpen={setIsEmailDrawerOpen}
        />
      </Container>
    </>
  )
}
